import { useState, useEffect } from 'preact/hooks';
import { route } from 'preact-router';
import type { RoutableProps } from 'preact-router';
import { BookOpen, Trash2, ArrowLeft, Sparkles } from 'lucide-preact';
import { getSavedStories, deleteStory } from '../lib/storage';
import { MAX_SAVED_STORIES } from '../lib/constants';
import type { Story } from '../lib/types';
import { ANIMALS, STORY_TYPES } from '../lib/constants';

function StoryCard({
  story,
  onRead,
  onDelete,
}: {
  story: Story;
  onRead: () => void;
  onDelete?: () => void;
}) {
  const animal = ANIMALS.find((a) => a.value === story.choices.animal);
  const storyType = STORY_TYPES.find((t) => t.value === story.choices.storyType);
  const date = new Date(story.createdAt).toLocaleDateString();

  return (
    <div className="bg-cream rounded-2xl border-ornate p-6 hover:shadow-story transition-all cursor-pointer group">
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-2xl bg-parchment border-ornate flex items-center justify-center flex-shrink-0">
          <span className="text-3xl" role="img" aria-label={animal?.label}>
            {animal?.icon}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-xl font-medium text-ink truncate group-hover:text-rust transition-colors">
            {story.title}
          </h3>
          <p className="text-sm text-ink/50 mt-1">
            {story.choices.childName} the {animal?.label} · {storyType?.label}
          </p>
          <p className="text-xs text-ink/30 mt-2">{date}</p>
        </div>
      </div>
      <div className="flex gap-3 mt-5">
        <button
          type="button"
          onClick={onRead}
          className="flex-1 px-4 py-3 text-sm font-medium text-cream bg-rust rounded-xl
                   hover:shadow-story transition-all cursor-pointer flex items-center justify-center gap-2"
        >
          <BookOpen size={16} />
          Read Story
        </button>
        {onDelete && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="px-4 py-3 text-sm font-medium text-ink/40 bg-parchment rounded-xl
                     hover:text-rust hover:bg-rust/10 transition-all cursor-pointer"
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>
    </div>
  );
}

export function Library(_props: RoutableProps) {
  const [stories, setStories] = useState<Story[]>([]);

  useEffect(() => {
    setStories(getSavedStories());
  }, []);

  const handleDelete = (id: string) => {
    deleteStory(id);
    setStories(getSavedStories());
  };

  return (
    <div className="min-h-screen gradient-warm">
      {/* Decorative background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-20 right-10 w-64 h-64 bg-gold/5 rounded-full blur-3xl float" />
        <div
          className="absolute bottom-40 left-10 w-80 h-80 bg-rust/5 rounded-full blur-3xl float"
          style={{ animationDelay: '2s' }}
        />
      </div>

      <div className="relative container-custom py-8 sm:py-12">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <header className="flex items-center justify-between mb-10 animate-in">
            <div>
              <h1 className="text-3xl sm:text-4xl font-display font-medium text-ink">My Stories</h1>
              <p className="text-ink/50 mt-2">
                {stories.length} of {MAX_SAVED_STORIES} stories saved
              </p>
            </div>
            <button
              onClick={() => route('/')}
              className="flex items-center gap-2 px-4 py-2 text-ink/50 hover:text-ink transition-colors cursor-pointer"
            >
              <ArrowLeft size={20} />
              <span className="hidden sm:inline">Home</span>
            </button>
          </header>

          {stories.length === 0 ? (
            <div
              className="bg-cream rounded-3xl border-ornate p-10 sm:p-14 text-center animate-in"
              style={{ animationDelay: '0.1s' }}
            >
              <div className="w-20 h-20 rounded-full bg-parchment border-ornate flex items-center justify-center mx-auto mb-6">
                <BookOpen className="w-10 h-10 text-ink/30" strokeWidth={1.25} />
              </div>
              <h2 className="text-2xl font-display font-medium text-ink mb-3">No stories yet</h2>
              <p className="text-ink/60 mb-8 max-w-sm mx-auto">
                Create your first magical story and it will appear here for you to read anytime
              </p>
              <button
                onClick={() => route('/create')}
                className="px-8 py-4 bg-rust text-cream rounded-2xl font-medium text-lg
                         shadow-story hover:shadow-story-hover transition-all
                         hover:-translate-y-1 cursor-pointer inline-flex items-center gap-3"
              >
                <Sparkles size={20} />
                Create a Story
              </button>
            </div>
          ) : (
            <div className="grid gap-4">
              {stories.map((story, index) => (
                <div
                  key={story.id}
                  className="animate-in"
                  style={{ animationDelay: `${0.1 + index * 0.05}s` }}
                >
                  <StoryCard
                    story={story}
                    onRead={() => route(`/read/${story.id}`)}
                    onDelete={() => handleDelete(story.id)}
                  />
                </div>
              ))}
            </div>
          )}

          {stories.length > 0 && stories.length < MAX_SAVED_STORIES && (
            <div className="mt-10 text-center animate-in" style={{ animationDelay: '0.3s' }}>
              <button
                onClick={() => route('/create')}
                className="px-6 py-3 bg-parchment text-ink rounded-2xl font-medium
                         border-ornate hover:shadow-story transition-all cursor-pointer
                         inline-flex items-center gap-2"
              >
                <Sparkles size={18} />
                Create Another Story
              </button>
            </div>
          )}

          <p
            className="mt-10 text-center text-sm text-ink/40 animate-in"
            style={{ animationDelay: '0.4s' }}
          >
            Stories are saved on this device only.
            <br />
            They will be lost if you clear your browser data.
          </p>
        </div>
      </div>
    </div>
  );
}
